import * as React from "react";

import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import CodeExecutionLogs from "@v2/features/page-renderer/components/code-execution/CodeExecutionLogs";
import { useAutoSaveHtmlContent } from "@v2/features/page-renderer/hooks/page-cache";
import { useNextDevServerHook } from "@v2/features/page-renderer/hooks/useNextDevServerHook";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { useLocation } from "react-router-dom";
import { useRequiredContext } from "replo-utils/react/context";
import * as z from "zod";

type NextDevServerContextType = ReturnType<typeof useNextDevServerHook>;

const NextDevServerContext = React.createContext<
  NextDevServerContextType | undefined
>(undefined);
NextDevServerContext.displayName = "NextDevServerContext";

const NextDevServerProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const originalProjectId = useCurrentProjectId();
  const location = useLocation();
  const currentProjectHostingType =
    useCurrentProjectContext()?.project?.hostingType;

  // Set projectId to undefined if URL starts with /editor
  const projectId = location.pathname.startsWith("/editor")
    ? undefined
    : originalProjectId;

  useAutoSaveHtmlContent({ projectId });

  const { project } = useCurrentProjectContext();

  const [showDevServerLogs, setShowDevServerLogs] = useLocalStorageState(
    "replo.debug.showNextDevServerLogs",
    false,
    { schema: z.boolean() },
  );

  const shouldShowDevServerLogs =
    showDevServerLogs && project?.hostingType === "replo";

  const result = useNextDevServerHook({
    projectId,
    hostingType: currentProjectHostingType,
  });

  return (
    <NextDevServerContext.Provider
      value={{
        ...result,
      }}
    >
      {children}
      {shouldShowDevServerLogs && (
        <CodeExecutionLogs onClose={() => setShowDevServerLogs(false)} />
      )}
    </NextDevServerContext.Provider>
  );
};

function useNextDevServer() {
  return useRequiredContext(NextDevServerContext);
}

export { NextDevServerContext, NextDevServerProvider, useNextDevServer };
